/**
 * Tools API
 * Connects to Azure Functions backend -> Cosmos DB
 */

import { apiFetch } from '../../../auth/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';
/**
 * Transform a raw Cosmos document (snake_case) into PWA format (camelCase).
 * `id` is the internal Cosmos document ID — preserved for writes.
 * `toolId` is supplier_tool_id — the human-facing ID shown in the UI and used in URLs.
 */
export function transformTool(tool) {
  return {
    id: tool.id,
    toolId: tool.supplier_tool_id || tool.tool_id,
    altToolId: tool.alt_tool_id,
    siteId: tool.site_id,
    supplierName: tool.site_name || null,
    toolType: tool.tool_type,
    oem: tool.oem,
    location: tool.site_location,
    preliminaryToolStatus: tool.preliminary_tool_status,
    workflowStatus: tool.workflow_status,
    reconStatus: tool.recon_status,
    supplierStatus: tool.supplier_status,
    supplierComments: tool.supplier_comments,
    reconComments: tool.supplier_comments,
    listNumber: tool.list_number,
    partDescription: tool.part_description,
    siteLocation: tool.site_location,
    detailLocation: tool.detail_location,
    sectionDetail: tool.detail_location,
    componentPartNumber1: tool.component_part_number_1,
    assetTag: null,
    photos: [],
    photoCount: 0,
    inventoryPartNumbers: [
      tool.inventory_part_number_1,
      tool.inventory_part_number_2,
      tool.inventory_part_number_3,
      tool.inventory_part_number_4
    ].filter(Boolean)
  };
}

export async function getTools(filters = {}, callerEmail = null) {
  const params = new URLSearchParams();
  if (callerEmail)          params.append('caller_email', callerEmail);
  if (filters.oem)          params.append('oem', filters.oem);
  if (filters.toolType)     params.append('tool_type', filters.toolType);
  if (filters.assetStatus)  params.append('preliminary_tool_status', filters.assetStatus);
  if (filters.searchText)   params.append('search', filters.searchText);

  const url = `${API_BASE_URL}/get_tools${params.toString() ? '?' + params.toString() : ''}`;
  const response = await apiFetch(url);
  if (!response.ok) throw new Error(`API error: ${response.status}`);

  const tools = await response.json();
  const transformedTools = tools.map(transformTool);
  return { tools: transformedTools, total: transformedTools.length };
}

export async function getToolById(supplierToolId, callerEmail = null) {
  const params = new URLSearchParams({ supplier_tool_id: supplierToolId });
  if (callerEmail) params.append('caller_email', callerEmail);

  const response = await apiFetch(`${API_BASE_URL}/get_tools?${params}`);
  if (!response.ok) throw new Error(`API error: ${response.status}`);

  const tools = await response.json();
  if (!tools.length) throw new Error('Tool not found');
  return transformTool(tools[0]);
}

export async function getFilterOptions(callerEmail = null) {
  const params = new URLSearchParams();
  if (callerEmail) params.append('caller_email', callerEmail);

  const response = await apiFetch(`${API_BASE_URL}/get_tools${params.toString() ? '?' + params.toString() : ''}`);
  if (!response.ok) throw new Error(`API error: ${response.status}`);

  const tools = await response.json();
  return {
    oems:         [...new Set(tools.map(t => t.oem))].filter(Boolean).sort(),
    toolTypes:    [...new Set(tools.map(t => t.tool_type))].filter(Boolean).sort(),
    assetStatuses:[...new Set(tools.map(t => t.preliminary_tool_status))].filter(Boolean).sort()
  };
}

export async function updateToolListNumber(internalId, listNumber, callerEmail = null) {
  const params = new URLSearchParams({ tool_id: internalId });
  if (callerEmail) params.append('caller_email', callerEmail);
  const response = await apiFetch(`${API_BASE_URL}/update_tool?${params}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ listNumber })
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update list number');
  }
  return response.json();
}
